// Bella Vita frontend — MOCK ADAPTER
// =============================================================================
// Implements the full BellaVitaAdapter contract against in-repo fixtures
// (src/mocks/*). Adds artificial latency and an optional error rate so the UI
// exercises real loading / error / retry states.
//
// This is DEVELOPMENT SCAFFOLDING — NOT production, NOT security, NOT a
// payment processor. The Odoo adapter will replace it wholesale.
// =============================================================================

import { CONFIG } from '../../config.js';
import { CATEGORIES, SERVICES, TAX } from '../../mocks/catalogue.mock.js';
import { SPECIALISTS } from '../../mocks/specialists.mock.js';
import { slotsForDay, isOpenDay, dayHasSlots, bookingWindow } from '../../mocks/availability.mock.js';
import { DEMO_BOOKINGS, DEMO_CUSTOMER } from '../../mocks/account.mock.js';
import { PAYMENT_METHODS } from '../../mocks/payments.mock.js';

const clone = (v) => JSON.parse(JSON.stringify(v));

function delay() {
  const ms = Math.max(0, CONFIG.mockLatencyMs) * (0.6 + Math.random() * 0.8);
  return new Promise((res) => setTimeout(res, ms));
}
async function gate() {
  await delay();
  if (CONFIG.mockErrorRate > 0 && Math.random() < CONFIG.mockErrorRate) {
    const e = new Error('MOCK_NETWORK_ERROR');
    e.code = 'MOCK_NETWORK_ERROR';
    throw e;
  }
}

function daysInMonthISO(monthISO) {
  const [y, m] = monthISO.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const out = [];
  for (let d = 1; d <= last; d++) {
    const date = new Date(y, m - 1, d);
    const dateISO = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    out.push({ dateISO, open: isOpenDay(date), hasSlots: dayHasSlots(date) });
  }
  return out;
}

/** Flatten either the V2 `people` payload or the legacy `picks` array to
 *  [{ serviceId, personId, specialistId|null, noPreference }]. */
function flattenItems(req) {
  if (Array.isArray(req.people) && req.people.length) {
    const out = [];
    req.people.forEach((grp) => {
      const pid = grp.person || grp.personId || 1;
      (grp.items || grp.services || []).forEach((it) => out.push({
        serviceId: it.serviceId ?? it.service_id,
        personId: pid,
        specialistId: it.specialistId ?? it.specialist_id ?? null,
        noPreference: !!(it.noPreference ?? it.no_preference),
      }));
    });
    return out;
  }
  return (req.picks || []).map((p) => ({
    serviceId: p.serviceId ?? p.service_id,
    personId: p.personId ?? p.person_index ?? 1,
    specialistId: p.specialistId ?? null,
    noPreference: !!p.noPreference,
  }));
}
function priceLines(items) {
  return items.map((p) => {
    const s = SERVICES.find((x) => x.id === p.serviceId) || { name: { ar: '', en: '' }, price: 0 };
    return { name: clone(s.name), price: s.price, person: p.personId };
  });
}
function totals(items) {
  const subtotal = items.reduce((a, p) => a + (SERVICES.find((s) => s.id === p.serviceId)?.price || 0), 0);
  const vat = Math.round(subtotal * TAX.vatRate);
  const total = subtotal + vat;
  const deposit = total;
  return { subtotal, vat, total, deposit };
}

// In-memory booking store for this session (created bookings + demo history).
const created = [];

let mockSession = null;

export const mockAdapter = {
  async getPricing() {
    await gate();
    return { vatRate: TAX.vatRate, depositRate: TAX.depositRate, currency: 'SAR' };
  },
  async getCategories() {
    await gate();
    return clone(CATEGORIES);
  },
  async getServices() {
    await gate();
    return clone(SERVICES);
  },
  async getService(id) {
    await gate();
    return clone(SERVICES.find((s) => s.id === id) || null);
  },
  async getSpecialists(/* { serviceIds } */) {
    await gate();
    // Mock has no capability matrix — every specialist "can do" every service.
    // Odoo adapter will filter by hr skills / service capability.
    return clone(SPECIALISTS);
  },
  async getPaymentMethods() {
    await gate();
    return clone(PAYMENT_METHODS);
  },
  async startPayment({ bookingRef, method }) {
    await gate();
    return { reference: `MOCK-${bookingRef}`, bookingRef, provider: method, state: 'done', redirectUrl: null };
  },
  async getPaymentStatus(reference, { bookingRef }) {
    await gate();
    return { payment: { reference, bookingRef, provider: 'mock', state: 'done', depositState: 'paid', paid: true } };
  },
  async bindMoyasarPayment(reference, { bookingRef, paymentId }) {
    await gate();
    return { reference, bookingRef, provider: 'moyasar', providerReference: paymentId, state: 'done', paid: true };
  },

  async getAvailability({ monthISO }) {
    await gate();
    return daysInMonthISO(monthISO);
  },
  async getSlots({ dateISO }) {
    await gate();
    const [y, m, d] = dateISO.split('-').map(Number);
    return slotsForDay(new Date(y, m - 1, d));
  },
  async getBookingWindow() {
    await gate();
    return bookingWindow();
  },
  async getNextAvailable() {
    await gate();
    const w = bookingWindow();
    const cur = new Date(w.firstDateISO);
    const end = new Date(w.lastDateISO);
    while (cur <= end) {
      const slots = slotsForDay(cur);
      const hit = slots.find((s) => s.available);
      if (hit) {
        const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
        return { dateISO: iso, time: hit.time, group: hit.group };
      }
      cur.setDate(cur.getDate() + 1);
    }
    return null;
  },

  async getCustomerAuthRoute() { return { action: 'signup_required' }; },
  async sendPortalInvite() { return { status: 'sent', action: 'login_required' }; },

  async quoteBookingDiscount({ code, serviceIds }) {
    await gate();
    const normalized = String(code || '').trim().toUpperCase();
    if (normalized !== 'WELCOME10') { const e = new Error('Invalid discount code'); e.code = 'discount_invalid'; e.status = 422; throw e; }
    const subtotal = (serviceIds || []).reduce((sum, id) => sum + (SERVICES.find((s) => s.id === id)?.price || 0), 0);
    const discount = Math.round(subtotal * 0.10);
    return { code: normalized, subtotal, discount, total: Math.max(subtotal - discount, 0) };
  },

  async createBooking(req) {
    await gate();
    const items = flattenItems(req);
    // resolve a display specialist per line (mock: honour explicit pick, else
    // deterministic pool pick = first specialist)
    const pool = SPECIALISTS.filter((s) => !s.anyPreference);
    const start = req.start || (req.dateISO && req.time ? `${req.dateISO}T${req.time}` : '');
    const [dateISO, time] = start.includes('T') ? start.split('T') : [req.dateISO, req.time];
    const t = totals(items);
    const promo = req.promoCode ? await this.quoteBookingDiscount({ code: req.promoCode, serviceIds: items.map((it) => it.serviceId) }) : null;
    if (promo) {
      t.vat = Math.round(Math.max(t.subtotal - promo.discount, 0) * TAX.vatRate);
      t.total = Math.max(t.subtotal - promo.discount, 0) + t.vat;
      t.deposit = t.total;
    }
    const ref = 'BV-' + (4800 + Math.floor(Math.random() * 1800));
    const svcLines = priceLines(items).map((ln, i) => {
      const it = items[i];
      const sp = it.specialistId
        ? SPECIALISTS.find((s) => s.id === it.specialistId)
        : pool[i % Math.max(1, pool.length)];
      return {
        ...ln,
        specialist: sp ? clone(sp.name) : null,
        assignmentSource: it.specialistId ? 'customer' : (it.noPreference ? 'no_preference' : 'auto'),
      };
    });
    const lead = svcLines.find((l) => l.specialist)?.specialist || null;
    const partySize = Math.max(1, ...items.map((i) => i.personId));
    const method = req.paymentMethod;
    const booking = {
      ref, dateISO, time, status: 'upcoming',
      partySize,
      specialist: lead,
      services: svcLines,
      subtotal: t.subtotal, discount: promo?.discount || 0, discountCode: promo?.code || null,
      total: t.total,
      deposit: t.deposit,
      depositPaid: method && method !== 'salon' ? t.deposit : 0,
      customer: { name: req.customer?.name || '', phone: req.customer?.phone || '' },
    };
    created.unshift(booking);
    return clone(booking);
  },

  async getBooking(ref) {
    await gate();
    const all = [...created, ...DEMO_BOOKINGS];
    return clone(all.find((b) => b.ref === ref) || null);
  },
  async getMyBookings({ scope = 'all' } = {}) {
    await gate();
    let all = [...created, ...DEMO_BOOKINGS];
    if (scope === 'upcoming') all = all.filter((b) => b.status === 'upcoming');
    if (scope === 'past') all = all.filter((b) => b.status !== 'upcoming');
    return clone(all);
  },
  async rescheduleBooking(ref, patch) {
    await gate();
    const b = created.find((x) => x.ref === ref) || DEMO_BOOKINGS.find((x) => x.ref === ref);
    if (!b) throw new Error('BOOKING_NOT_FOUND');
    if (patch.dateISO) b.dateISO = patch.dateISO;
    if (patch.time) b.time = patch.time;
    return clone(b);
  },
  async cancelBooking(ref) {
    await gate();
    return { ref, status: 'cancelled' };
  },

  // --- mock auth (NOT production security) ---
  async requestOtp() {
    await gate();
    return { sent: true, expiresInSec: 120 };
  },
  async verifyOtp({ phone }) {
    await gate();
    mockSession = { token: 'mock.' + Math.random().toString(36).slice(2), customer: { ...DEMO_CUSTOMER, phone } };
    return clone(mockSession);
  },
  async login({ email }) {
    await gate();
    mockSession = { token: 'mock.' + Math.random().toString(36).slice(2), customer: { ...DEMO_CUSTOMER, email } };
    return clone(mockSession);
  },
  async logout() {
    await gate();
    mockSession = null;
  },
  async currentSession() {
    return mockSession ? clone(mockSession) : null;
  },
};

export default mockAdapter;
